import React, { useState, useEffect } from "react";
import styles from "./FeedItemContentsDropdown.module.css";
import ChevronButton from "../ChevronButton";
import classNames from "classnames";
import SectionEditorDropdown from "../SectionEditorDropdown";
import Button from "../Button";
import PropTypes from "prop-types";

/**
 * Dropdown that allows a user to edit the contents of a Standing Order or Standard Content feed item.
 */
export function FeedItemContentsDropdown({
  sections = [], // Add fallback default for empty arrays
  setSections,
  isSectionOpen,
  onClick,
}) {
  const [isOpen, setIsOpen] = useState(isSectionOpen);

  // sets value to index of current section being edited
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    // console.log("Edit Change:", edit);
  }, [edit]);

  // const [itemSections, setItemSections] = useState(sections);
  const [itemSections, setItemSections] = useState(sections || []); // Update to include safety checks for rendering delays
  useEffect(() => {
    // console.log("Item Sections Updated:", itemSections);
  }, [itemSections]);

  useEffect(() => {
    setItemSections(sections || []);
  }, [sections]);

  // This is very delayed, need to work on it more
  function reindex(contents) {
    return contents.map((item, i) => ({ ...item, index: i }));
  }

  const onMoveUp = (index) => {
    if (index <= 0) return; // can't move up the first item

    let newContents = [...itemSections];

    // swap items in array
    [newContents[index - 1], newContents[index]] = [
      newContents[index],
      newContents[index - 1],
    ];

    newContents = reindex(newContents);

    setItemSections(newContents);
    setSections(newContents);
  };

  const onMoveDown = (index) => {
    if (index >= itemSections.length - 1) return; // can't move down the last item

    let newContents = [...itemSections];

    // swap items in array
    [newContents[index], newContents[index + 1]] = [
      newContents[index + 1],
      newContents[index],
    ];

    newContents = reindex(newContents);

    setItemSections(newContents);
    setSections(newContents);
  };

  const onMove = (position, newPosition) => {
    let newContents = [...itemSections];

    // remove the item
    const [moved] = newContents.splice(position, 1);

    // insert at the new position
    newContents.splice(newPosition, 0, moved);

    newContents = reindex(newContents);

    setItemSections(newContents);
    setSections(newContents);
  };

  const setSectionContent = (index, newSections) => {
    const newContents = itemSections.map((section, sectionIndex) =>
      sectionIndex === index
        ? {
            ...section,
            elements: newSections,
          }
        : section
    );
    setItemSections(newContents);
    setSections(newContents);
  };

  // Store background color as full rgba() string for consistency
  const setSectionBackgroundColor = (index, backgroundColor) => {
    let newContents = [...itemSections];
    if (!newContents[index]) {
      // console.log(`ERROR: No section at index ${index}`);
      return;
    }

    const raw = backgroundColor == null ? "" : String(backgroundColor);

    // Normalize to rgba() format
    let normalized = "";
    if (raw) {
      // If already in rgba/rgb format, use as-is
      if (raw.startsWith("rgba(") || raw.startsWith("rgb(")) {
        normalized = raw;
      } else {
        // If it's just comma-separated values, wrap in rgba()
        const cleaned = raw
          .replace(/\s+/g, "") // remove all whitespace
          .replace(/,+/g, ",") // collapse multiple commas
          .replace(/^,|,$/g, ""); // trim leading/trailing commas
        normalized = cleaned ? `rgba(${cleaned})` : "";
      }
    }

    // Don't update if the value is the same
    if (newContents[index]?.backgroundColor === normalized) {
      // console.log(`No change needed - same color`);
      return;
    }

    newContents[index] = { ...newContents[index], backgroundColor: normalized };

    setItemSections(newContents);
    setSections(newContents);
  };

  const onDelete = (removeIndex) => {
    setItemSections((prev) => {
      const without = prev.filter((_, i) => i !== removeIndex);

      const reindexed = without.map((s, i) => ({ ...s, index: i }));

      setEdit(null);
      setSections(reindexed, "sections");
      return reindexed;
    });
  };

  const onClone = (index) => {
    const source = itemSections[index];
    if (!source) return;

    const beforeClone = itemSections.map((item, itemIndex) =>
      itemIndex > index
        ? {
            ...item,
            index: item.index + 1,
          }
        : item
    );

    const toClone = {
      ...source,
      index: source.index + 1,
      elements: Array.isArray(source.elements) ? [...source.elements] : [],
    };

    const newContents = [
      ...beforeClone.slice(0, index + 1),
      toClone,
      ...beforeClone.slice(index + 1),
    ];

    setItemSections(newContents);
    setSections(newContents);
  };

  const [addSectionPositioningCaller, setAddSectionPositioningCaller] =
    useState(1);

  const onAdd = (position) => {
    let newContents = [...itemSections];

    if (position === "first") {
      // bump all existing indexes up by 1
      newContents = newContents.map((item) => ({
        ...item,
        index: item.index + 1,
      }));

      // add new item at index 0
      newContents.unshift({
        elements: [],
        // backgroundColor: "rgba(127,106,69,1.00)", //old brown default
        backgroundColor: "rgba(255,255,255,1.00)",
        hidden: false,
        index: 0,
      });

      setEdit(0); // focus on the new first item
    } else if (position === "last") {
      const lastIndex =
        newContents.length > 0
          ? Math.max(...newContents.map((i) => i.index)) + 1
          : 0;

      // add new item at the end with the next index
      newContents.push({
        elements: [],
        backgroundColor: "rgba(255,255,255,1.00)",
        hidden: false,
        index: lastIndex,
      });

      setEdit(lastIndex); // focus on the new last item
    }

    setItemSections(newContents);
    setSections(newContents);
  };

  const onHide = (index, state) => {
    const newContents = itemSections.map((section, sectionIndex) =>
      sectionIndex === index
        ? {
            ...section,
            hidden: state,
          }
        : section
    );

    setItemSections(newContents);
    setSections(newContents);
  };

  useEffect(() => {
    if (addSectionPositioningCaller !== 1) {
      let newContents = [...itemSections];
      var update = newContents.findIndex((item) => item.index == 0);
      if (update != undefined) {
        onMove(0, 1);
      }
    }
  }, [addSectionPositioningCaller]);

  return (
    <>
      <>
        <div className={styles.container}>
          <div
            className={classNames(styles.content, {
              [styles["dropdown-container"]]: isSectionOpen,
            })}
            onClick={() => {
              if (!isSectionOpen) {
                onClick("contents");
                setIsOpen(true);
              }
            }}
          >
            <div></div>
            <div
              className={classNames({
                [styles.hidden]: isOpen,
              })}
            >
              <p className={styles.title}>Contents:</p>
              <ul className={styles.list}>
                {itemSections &&
                  itemSections
                    .filter((section) => Array.isArray(section.elements)) // Filter out elements with unexpected structure
                    .sort((a, b) => a.index - b.index)
                    .map((section) => (
                      <li className={styles.listItem} key={section.index}>
                        Section{" "}
                        {itemSections[0].index == 1
                          ? section.index
                          : section.index + 1}
                        : {section.elements.map((item) => item.type + " ")}
                      </li>
                    ))}
              </ul>
            </div>
            <div
              className={classNames({
                [styles.hidden]: !isOpen,
              })}
            >
              <p className={styles.title}>Contents:</p>
              <Button
                className={styles.btnStyled}
                text={"Add Section"}
                onClick={() => onAdd("first")}
              />
              <div className={styles.hr}></div>

              {itemSections &&
                [...itemSections]
                  .sort((a, b) => (a.index > b.index ? 1 : -1))
                  .map((section) => {
                    const actualIndex = section.index;
                    return (
                      <div key={section.index}>
                        <SectionEditorDropdown
                          onClone={onClone}
                          onDelete={onDelete}
                          onMove={onMove}
                          onSectionUp={onMoveUp}
                          onSectionDown={onMoveDown}
                          setSectionContent={setSectionContent}
                          setSectionBackgroundColor={setSectionBackgroundColor}
                          isHidden={section.hidden}
                          onHide={onHide}
                          length={itemSections.length}
                          index={actualIndex}
                          section={section}
                          editing={edit == actualIndex ? true : false}
                          setEdit={setEdit}
                          setSections={setSections}
                          sections={sections}
                        />
                      </div>
                    );
                  })}
              <Button
                className={styles.btnStyled2}
                text={"Add Section"}
                onClick={() => onAdd("last")}
              />
            </div>
          </div>
          <div className={styles.chevron}>
            <ChevronButton
              onClick={() => {
                onClick(isOpen ? "close" : "contents");
                setIsOpen((isOpen) => !isOpen);
              }}
              orientation={isOpen ? "down" : "right"}
            />
          </div>
        </div>
      </>
      {!isOpen && <hr className={styles.hr2} />}
    </>
  );
}

// FeedItemContentsDropdown.propTypes = {};
// Define PropTypes
FeedItemContentsDropdown.propTypes = {
  sections: PropTypes.array,
  setSections: PropTypes.func,
  isSectionOpen: PropTypes.bool,
  onClick: PropTypes.func,
};
